<template>
  <ShellProjectPerformersInfo
    title="Проекты"
    :projectData="this.tempProjectData"
    :basicData="this.tempBasicData"
    v-if="isCommonInfoOpen"
    @close="handleClose"
  />
  <div v-else>
    <div class="header">
      <div class="header__item">
        <CustomSelect
          :options="this.residentsOptions"
          label="ЖК"
          option-label-key="resident_name"
          option-value-key="resident_id"
          @change="value => this.onChange('resident_id', value)"
          :value="this.filters.resident_id"
        />
      </div>
      <div class="header__item">
        <CustomSelect
          :options="this.projectTypesOptions"
          label="Тип проекта"
          option-label-key="project_type_name"
          option-value-key="project_type_id"
          @change="value => this.onChange('project_type_id', value)"
          :value="this.filters.project_type_id"
          />
      </div>
    </div>
    <CustomTable
      title="Проекты"
      :rowsData="this.rowsData"
      :columns="this.projectsColumns"
      :loading="isFetching"
      :small="true"
      :show-search="true"
      :custom-body="true"
      :sticky-header="true"
      :add-btn="true"
      :on-add-click="this.handleAddClick"
      :update-data="this.getData"
    >
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.project_id }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            <ExpandableText :text="props.row.project_name" :maxLines="1" />
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.project_short_name }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.project_type_name }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.resident_name }}
          </custom-table-cell>
          <custom-table-cell
            align="left"
            vertical-align="center"
            :class="getFillClass(props.row.is_filled)"
          >
            <template v-if="Array.isArray(props.row.entrances)">
              <q-btn
                v-for="(entrance, index) in props.row.entrances"
                :key="index"
                :label="entrance"
                :style="{ fontSize: '14px' }"
                class="q-mx-xs"
                color="primary"
                flat
                dense
                @click="handleEntranceClick(props.row, entrance)"
              />
            </template>
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            {{ props.row.avans }}  
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            {{ props.row.start_date }}
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            {{ props.row.finish_date }}
          </custom-table-cell>
          <custom-table-cell align="center" title="Документы" vertical-align="center">
            <BlockItem>
              <q-btn label="Скачать документ" color="green" size="sm"
                padding="xs" no-wrap :loading="infoLoading[props.row.project_id]"
                @click="handleClickDoc(props.row.project_id)" />
            </BlockItem>
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <CustomIcon
              icon="edit"
              green
              title="Редактировать"
              @click="handleEditClick(props.row)"
            />
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <CustomIcon
              icon="user"
              primary
              title="Изменить назначение"
              @click="handleProjectClick(props.row)"
              :loading="this.infoLoading[props.row.project_id]"
            />
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script>
import ShellProjectPerformersInfo from "@/views/ShellProjects/ShellProjectPerformersInfo";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomIcon from "@/components/common/CustomIcon";
import Clickable from "@/components/common/Clickable";
import CheckOrClose from "@/components/common/CheckOrClose";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { appActions } from "@/services/store/modules/app_module/app_actions";
import { MODAL_NAMES } from "@/constants";
import {
  projectsColumns,
  getAllProjects,
  getProjectInfo,
  updateProject,
  getProjectTypes,
  copyPasteEntranceProperties,
} from "@/views/ShellProjects/services";
import { downloadFile } from "@/services";
import ExpandableText from "@/components/common/ExpandableText.vue";
import CustomSelect from "@/components/form/CustomSelect";
import { getResidencyOptions } from "../FlatList/services";

export default {
  name: "ShellProjects",
  components: {
    ShellProjectPerformersInfo,
    CheckOrClose,
    Clickable,
    CustomIcon,
    CustomTableCell,
    CustomTable,
    CustomSelect,
    ExpandableText,
  },
  data() {
    return {
      projectsColumns,
      rowsData: [],
      isFetching: false,
      tempBasicData: {},
      tempProjectData: {},
      isCommonInfoOpen: false,
      infoLoading: {},
      filters: {
        resident_id: '', 
        project_type_id: ''
      },
      residentsOptions: [],
      projectTypesOptions: [],
    };
  },
  methods: {
    async getData() {
      this.isFetching = true;
      const res = await getAllProjects(this.filters);
      this.isFetching = false;

      if (res) {
        this.rowsData = res;
      }
    },
    async getSelectsOptions() {
      const residentsOptions = await getResidencyOptions();
      const projectTypesOptions = await getProjectTypes();

      this.residentsOptions = !residentsOptions ? [] : residentsOptions;
      this.projectTypesOptions = !projectTypesOptions ? [] : projectTypesOptions;
    },
    onChange(key, value) {
      this.filters[key] = value;
      this.getData();
    },
    async handleAddClick() {
      this.$store.commit(
        appActions.showModal(MODAL_NAMES.UPD_SHL_PROJECTS, {
          onSubmit: this.onAddSubmit,
          title: "Добавить проект",
        })
      );
    },
    async handleEditClick(row) {
      this.$store.commit(
        appActions.showModal(MODAL_NAMES.UPD_SHL_PROJECTS, {
          onSubmit: (body) => this.onUpdateSubmit(body),
          title: "Изменить проект",
          body: row,
        })
      );
    },
    async handleProjectClick(row) {
      this.infoLoading[row.project_id] = true;
      const projInfo = await getProjectInfo(row.project_id);
      this.tempBasicData = {
        project_id: row.project_id,
        project_name: row.project_name,
        resident_name: row.resident_name,
        entrances: row.entrances,
      };
      this.tempProjectData = projInfo;
      this.infoLoading[row.project_id] = false;
      if (!projInfo) return;
      this.isCommonInfoOpen = true;
    },
    async handleEntranceClick(row, entrance) {
      this.$store.commit(
        appActions.showModal(MODAL_NAMES.COPY_PASTE_ENTRANCE_PROPERTIES, {
          onSubmit: (body) => this.onCopyPasteEntarancePropertiesSubmit(body),
          title: "Дублирование подъезда",
          body: {...row, entrance: entrance}
        })
      );
    },
    handleClose() {
      this.getData();
      this.tempBasicData = {};
      this.tempProjectData = {};
      this.isCommonInfoOpen = false;
    },
    async onAddSubmit(body) {
      const res = await updateProject(body);
      if (!res) return;
      this.rowsData = res;
      this.showSucceedSnackbar();
      this.$store.commit(appActions.closeModal());
    },
    async onUpdateSubmit(body) {
      const res = await updateProject(body);
      if (!res) return;
      this.rowsData = res;
      this.showSucceedSnackbar();
      this.$store.commit(appActions.closeModal());
    },
    async onCopyPasteEntarancePropertiesSubmit(body) {
      const res = await copyPasteEntranceProperties(body);
      if (!res) return;
      this.rowsData = res;
      this.showSucceedSnackbar();
      this.$store.commit(appActions.closeModal());
    },
    async handleClickDoc(project_id) {
      this.infoLoading[project_id] = true;

      await downloadFile(`/floor_map/project/agreement/project_id/${project_id}/`, 'Договор', {
        responseType: "arraybuffer"
      }, 'docx', 'get')

      this.infoLoading[project_id] = false;
    },
    getFillClass(is_filled) {
      if (+is_filled === 1) return "fill-yellow";
      if (+is_filled === 2) return "fill-green";
      return "";
    },
  },
  mounted() {
    this.getSelectsOptions();
    this.getData();
  },
  setup() {
    const { showSucceedSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar };
  },
};
</script>


<style scoped lang="scss">
.header {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  &__item {
    width: 200px;
  }
  @media (max-width: 400px) {
    &__item {
      width: 100%;
    }
  }
}

.fill-yellow {
  background-color: #fff3cd;
  color: #856404;
  font-weight: bold;
}

.fill-green {
  background-color: #d4edda;
  color: #155724;
  font-weight: bold;
}

.text-lg span {
  font-size: "16px";
}
</style>
